import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  TrendingUp, IndianRupee, CreditCard, CheckCircle, XCircle, AlertTriangle,
  PieChart, BarChart3, Calendar, Download, RotateCcw
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, PieChart as RechartPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#0f766e', '#14b8a6', '#f59e0b', '#f43f5e', '#6366f1'];

export default function PaymentAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/payments', { 
        params: { from_date: fromDate, to_date: toDate } 
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching payment analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="payment-analytics-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const methods = analytics?.payment_methods || {};
  const online = analytics?.online_payments || {};
  const trend = analytics?.daily_trend || [];
  const failures = analytics?.top_failures || [];

  // Prepare chart data
  const methodsData = Object.entries(methods).map(([name, value]) => ({ name, value }));
  const successRate = online.success_rate || 0;

  return (
    <div className="space-y-6" data-testid="payment-analytics-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Payment Analytics</h1>
          <p className="text-sm text-muted-foreground">Track payment success, methods, and revenue trends</p>
        </div>

        {/* Date Range Filters — wrap & stack on mobile */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1 flex-1 min-w-[120px]">
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-8 text-xs" data-testid="analytics-from-date" />
          </div>
          <div className="space-y-1 flex-1 min-w-[120px]">
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-8 text-xs" data-testid="analytics-to-date" />
          </div>
          <Button size="sm" onClick={fetchAnalytics} className="h-8 shrink-0" data-testid="analytics-refresh"><Download className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border-emerald-200 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-emerald-600 font-medium">Collected</span>
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-emerald-700">
              ₹{summary.total_collected?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-emerald-600 mt-1">{summary.paid_count || 0} payments</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-amber-600 font-medium">Pending</span>
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-amber-700">
              ₹{summary.total_pending?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-amber-600 mt-1">{summary.pending_count || 0} unpaid</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-blue-600 font-medium">Success Rate</span>
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-blue-700">
              {successRate}%
            </p>
            <p className="text-xs text-blue-600 mt-1">{online.successful || 0} of {online.attempted || 0} online</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/20 dark:to-red-950/20 border-rose-200 hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-rose-600 font-medium">Refunded</span>
              <RotateCcw className="h-4 w-4 text-rose-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-rose-700">
              ₹{summary.total_refunded?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-rose-600 mt-1">{summary.refunded_count || 0} refunds</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Daily Collection Trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Daily Collection Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => '₹' + (v / 1000).toFixed(0) + 'K'} />
                  <Tooltip formatter={(value) => ['₹' + value.toLocaleString()]} />
                  <Line type="monotone" dataKey="amount" stroke="#0f766e" strokeWidth={2} dot={{ fill: '#0f766e', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                No payment data in selected period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PieChart className="h-4 w-4 text-primary" />
              Payment Methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            {methodsData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <RechartPie>
                    <Pie 
                      data={methodsData} 
                      cx="50%" 
                      cy="50%" 
                      innerRadius={50} 
                      outerRadius={80} 
                      paddingAngle={2} 
                      dataKey="value"
                    >
                      {methodsData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => ['₹' + value.toLocaleString()]} />
                  </RechartPie>
                </ResponsiveContainer>
                <div className="space-y-2 mt-4">
                  {methodsData.map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="font-medium tabular-nums">₹{item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
                No payment method data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Online Payments Performance */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Online Payment Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Attempted</span>
              <span className="font-semibold tabular-nums">{online.attempted || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Successful
              </span>
              <span className="font-semibold tabular-nums text-emerald-600">{online.successful || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5 text-red-600" /> Failed
              </span>
              <span className="font-semibold tabular-nums text-red-600">{online.failed || 0}</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Success Rate</span>
                <Badge className={`text-xs ${successRate >= 90 ? 'bg-emerald-600' : successRate >= 70 ? 'bg-amber-600' : 'bg-red-600'}`}>
                  {successRate}%
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Revenue Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Collected</span>
              <span className="font-semibold tabular-nums text-emerald-600">₹{summary.total_collected?.toLocaleString() || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Refunded</span>
              <span className="font-semibold tabular-nums text-rose-600">₹{summary.total_refunded?.toLocaleString() || 0}</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Net Revenue</span>
                <span className="font-bold tabular-nums text-lg text-emerald-700">₹{summary.net_revenue?.toLocaleString() || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Top Payment Failures
            </CardTitle>
          </CardHeader>
          <CardContent>
            {failures.length > 0 ? (
              <div className="space-y-2">
                {failures.slice(0, 5).map((fail, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900">
                    <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-red-800 dark:text-red-200">{fail.reason}</p>
                      <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5">{fail.count} occurrence{fail.count > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle className="h-8 w-8 text-emerald-500 mb-2" />
                <p className="text-sm font-medium text-emerald-700">No payment failures!</p>
                <p className="text-xs text-muted-foreground">100% success rate</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Collection Insights */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Quick Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <CreditCard className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                    Online payments are {successRate >= 90 ? 'performing excellently' : successRate >= 70 ? 'performing well' : 'below target'}.
                  </p>
                  <p className="text-[10px] text-blue-700 dark:text-blue-300 mt-1">
                    {online.successful || 0} successful transactions
                  </p>
                </div>
              </div>
            </div>

            {summary.total_refunded > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <RotateCcw className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
                      {summary.refunded_count} refund{summary.refunded_count > 1 ? 's' : ''} processed
                    </p>
                    <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                      {((summary.total_refunded / summary.total_collected) * 100).toFixed(1)}% of revenue
                    </p>
                  </div>
                </div>
              </div>
            )}

            {online.failed > 0 && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-red-900 dark:text-red-100">
                      {online.failed} payment{online.failed > 1 ? 's' : ''} failed
                    </p>
                    <p className="text-[10px] text-red-700 dark:text-red-300 mt-1">
                      Review failures to identify issues
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-2">
                <IndianRupee className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-purple-900 dark:text-purple-100">
                    Net Revenue: ₹{summary.net_revenue?.toLocaleString() || 0}
                  </p>
                  <p className="text-[10px] text-purple-700 dark:text-purple-300 mt-1">
                    After refunds
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
